import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import type { IScannerControls } from "@zxing/browser";
import { compareBarcodes, normalizeBarcode, type BarcodeConfidence } from "../shared/barcodes";
import type { BarcodeIndexItem, BarcodeIndexResponse } from "../shared/types";
import { loadBarcodeIndex } from "./barcodeIndex";

interface RecallMatch {
  confidence: BarcodeConfidence;
  recall: BarcodeIndexItem;
}

interface ScanOutcome {
  barcode: string;
  matches: RecallMatch[];
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "America/New_York",
  }).format(new Date(value));
}

export function findRecallMatches(
  barcode: string,
  index: BarcodeIndexResponse,
): RecallMatch[] {
  return index.items.flatMap((recall) => {
    let confidence: BarcodeConfidence | null = null;
    for (const published of recall.barcodes) {
      const candidate = compareBarcodes(barcode, published);
      if (candidate === "exact") {
        confidence = "exact";
        break;
      }
      if (candidate === "possible") confidence = "possible";
    }
    return confidence ? [{ confidence, recall }] : [];
  }).sort((left, right) => {
    if (left.confidence !== right.confidence) return left.confidence === "exact" ? -1 : 1;
    return right.recall.publishedAt.localeCompare(left.recall.publishedAt);
  });
}

export function BarcodeScanner() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraStatus, setCameraStatus] = useState("CAMERA OFF");
  const [manualBarcode, setManualBarcode] = useState("");
  const [outcome, setOutcome] = useState<ScanOutcome | null>(null);
  const [message, setMessage] = useState("");
  const [indexMeta, setIndexMeta] = useState<{ count: number; stale: boolean; generatedAt: string } | null>(null);

  const stopCamera = useCallback(() => {
    controlsRef.current?.stop();
    controlsRef.current = null;
    setCameraOpen(false);
    setCameraStatus("CAMERA OFF");
  }, []);

  const evaluate = useCallback(async (rawValue: string) => {
    const barcode = normalizeBarcode(rawValue);
    if (!barcode) {
      setOutcome(null);
      setMessage("ENTER A BARCODE CONTAINING 8 TO 14 DIGITS.");
      return;
    }
document.dispatchEvent(new CustomEvent("somewhere:analytics", { detail: "barcode_check_started" }));
    setMessage("CHECKING THE LOCAL RECALL INDEX…");
    try {
      const loaded = await loadBarcodeIndex();
      setIndexMeta({
        count: loaded.data.items.length,
        stale: loaded.stale,
        generatedAt: loaded.data.generatedAt,
      });
      setOutcome({ barcode, matches: findRecallMatches(barcode, loaded.data) });
      document.dispatchEvent(new CustomEvent("somewhere:analytics", { detail: "barcode_check_completed" }));
      setManualBarcode(barcode);
      setMessage(loaded.stale ? "OFFLINE — USING THE LAST SAVED INDEX." : "");
    } catch {
      setOutcome(null);
document.dispatchEvent(new CustomEvent("somewhere:analytics", { detail: "barcode_check_failed" }));
      setMessage("THE RECALL INDEX COULD NOT LOAD. TRY AGAIN WITH A CONNECTION.");
    }
  }, []);

  useEffect(() => {
    if (!cameraOpen) return;
    let cancelled = false;

    const start = async () => {
document.dispatchEvent(new CustomEvent("somewhere:analytics", { detail: "camera_open" }));
      setCameraStatus("STARTING CAMERA…");
      setMessage("");
      try {
        const [{ BrowserMultiFormatReader }, library] = await Promise.all([
          import("@zxing/browser"),
          import("@zxing/library"),
        ]);
        const hints = new Map();
        hints.set(library.DecodeHintType.POSSIBLE_FORMATS, [
          library.BarcodeFormat.EAN_8,
          library.BarcodeFormat.EAN_13,
          library.BarcodeFormat.UPC_A,
          library.BarcodeFormat.UPC_E,
          library.BarcodeFormat.DATA_MATRIX,
        ]);
        const reader = new BrowserMultiFormatReader(hints, {
          delayBetweenScanAttempts: 100,
          delayBetweenScanSuccess: 500,
        });
        const video = videoRef.current;
        if (!video) throw new Error("Camera preview is unavailable");
        const controls = await reader.decodeFromVideoDevice(undefined, video, (result, error, activeControls) => {
          if (result && !cancelled) {
            activeControls.stop();
            controlsRef.current = null;
            setCameraOpen(false);
            setCameraStatus("BARCODE CAPTURED");
            void evaluate(result.getText());
            return;
          }
          if (error && error.name !== "NotFoundException" && !cancelled) {
            setCameraStatus("KEEP THE BARCODE INSIDE THE FRAME");
          }
        });
        if (cancelled) controls.stop();
        else {
          controlsRef.current = controls;
          setCameraStatus("POINT AT A UPC, EAN, OR DATA MATRIX CODE");
        }
      } catch (error) {
        if (cancelled) return;
        setCameraOpen(false);
        setCameraStatus("CAMERA UNAVAILABLE");
        setMessage(
          error instanceof DOMException && error.name === "NotAllowedError"
            ? "CAMERA PERMISSION WAS DENIED. ENTER THE BARCODE BELOW."
            : "CAMERA COULD NOT START. ENTER THE BARCODE BELOW.",
        );
      }
    };

    void start();
    return () => {
      cancelled = true;
      controlsRef.current?.stop();
      controlsRef.current = null;
    };
  }, [cameraOpen, evaluate]);

  useEffect(() => () => controlsRef.current?.stop(), []);

  const submitManual = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    stopCamera();
    void evaluate(manualBarcode);
  };

  const exactMatches = outcome?.matches.filter((match) => match.confidence === "exact") ?? [];
  const possibleMatches = outcome?.matches.filter((match) => match.confidence === "possible") ?? [];

  return (
    <section className="barcode-check" id="barcode-check" tabIndex={-1} aria-labelledby="barcode-check-title">
      <div className="barcode-intro">
        <div>
          <div className="barcode-kicker">ON-DEVICE RECALL CHECK</div>
          <h2 id="barcode-check-title">SCAN YOUR FOOD.</h2>
        </div>
        <p>
          Your camera and barcode stay on this device. We compare the code with a cached index
          of identifiers published in official FDA and USDA recall notices.
        </p>
      </div>

      <div className="barcode-controls">
        <button
          className="scan-button"
          type="button"
          aria-expanded={cameraOpen}
          onClick={() => cameraOpen ? stopCamera() : setCameraOpen(true)}
        >
          {cameraOpen ? "CLOSE CAMERA ×" : "SCAN WITH CAMERA →"}
        </button>
        <form className="barcode-manual" onSubmit={submitManual}>
          <label htmlFor="manual-barcode">OR ENTER THE NUMBERS</label>
          <div>
            <input
              id="manual-barcode"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              spellCheck={false}
              value={manualBarcode}
              placeholder="0 12345 67890 5"
              onChange={(event) => setManualBarcode(event.target.value)}
            />
            <button type="submit">CHECK →</button>
          </div>
        </form>
      </div>

      {cameraOpen && (
        <div className="camera-panel">
          <video ref={videoRef} muted playsInline aria-label="Live barcode camera preview" />
          <div className="camera-target" aria-hidden="true"><i /></div>
          <div className="camera-status">{cameraStatus}</div>
        </div>
      )}

      <div className="scanner-message" aria-live="polite">
        {message && <p>{message}</p>}
        {indexMeta && (
          <span>
            {indexMeta.count.toLocaleString()} BARCODE-LINKED RECORDS / INDEX {indexMeta.stale ? "SAVED " : "UPDATED "}
            {formatDate(indexMeta.generatedAt)}
          </span>
        )}
      </div>

      {outcome && (
        <div
          className={`scan-result ${exactMatches.length > 0 ? "has-match" : possibleMatches.length > 0 ? "has-possible" : "no-match"}`}
          role="status"
          aria-live="polite"
        >
          <div className="scan-result-heading">
            <span>SCANNED / {outcome.barcode}</span>
            <h3>
              {exactMatches.length > 0
                ? "RECALL RECORD FOUND."
                : possibleMatches.length > 0
                  ? "POSSIBLE IDENTIFIER MATCH."
                  : "NO RECALL MATCH FOUND."}
            </h3>
            <p>
              {outcome.matches.length > 0
                ? "Do not consume this item until you compare its lot and date codes with the official notice."
                : "This is not a safety guarantee. A recall may omit a barcode, and new hazards may not have been announced yet."}
            </p>
          </div>
          {outcome.matches.length > 0 && (
            <div className="scan-matches">
              {outcome.matches.map(({ confidence, recall }) => (
                <article key={recall.id}>
                  <div className="scan-match-meta">
                    <span>{confidence === "exact" ? "GTIN MATCH" : "INCOMPLETE PUBLISHED CODE"}</span>
                    <span>{recall.agency}</span>
                    <span>{formatDate(recall.publishedAt)}</span>
                  </div>
                  <h4><a href={`/recalls/${recall.id}`}>{recall.title}</a></h4>
                  {recall.reason && <p>{recall.reason}</p>}
                  <a href={recall.sourceUrl} target="_blank" rel="noreferrer">VERIFY LOT + DATE AT OFFICIAL SOURCE ↗</a>
                </article>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
